import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Card } from '@/constants';
import {
  supportedCountries,
  supportedLocales,
  langs,
} from '@/constants/supportedLocales';
import { mycaCardTypes, lineOfBusinessTypes } from '@/constants/productMapping';
import { WAITLIST_SCROLL_RESTORE_TTL_MS } from '@/constants/waitlistLounges';
import { isGuestBenefitEligibleAccount } from './accountHelper';

/**
 * Generates a safe anchor ID for navigation and content linking.
 * @param {Object} params
 * @param {'chef'|'partner'|'faq'} params.type
 * @param {string} [params.name]
 * @param {string} [params.title]
 * @param {number} [params.idx]
 * @param {string} [params.section]
 * @returns {string}
 */
// Polyfill for CSS.escape for SSR/Node environments
function cssEscape(str) {
  // Always perform a consistent normalization (server & client) so anchor IDs
  // are stable regardless of environment or the presence of window.CSS.escape.
  const s = String(str || '');
  return s
    .normalize('NFKD')
    .replace(/[^\dA-Za-z-]/g, '-') // replace non-alnum/dash with dash
    .replace(/-+/g, '-') // collapse multiple dashes
    .replace(/^-+|-+$/g, ''); // trim leading/trailing dashes
}

export function getAnchorId({ type, name, title, idx, section }) {
  const escapedString = (s) =>
    s ? cssEscape(s.replace(/\s+/g, '-').toLowerCase()) : idx;
  if (type) {
    return `${type}-${escapedString(name || title)}`;
  }
  return cssEscape(`${section || ''}-${idx}`);
}

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function getLang(locale) {
  if (locale === 'fr-CA') {
    return 'fr-FR';
  }
  return locale;
}

export const getSafeLocaleUtil = (unsafeLocale) => {
  try {
    const matchingLocale = supportedLocales.find(
      (locale) => locale.toLowerCase() === unsafeLocale.toLowerCase()
    );
    if (matchingLocale) {
      return matchingLocale;
    }
    // eslint-disable-next-line no-empty -- just using try to cover multiple non-breaking cases
  } catch (e) {}
  return null;
};

export const isInvalidLoungeFinderRoute = ({ locale, product }) => {
  if (!locale || !product) {
    return true;
  }

  const localeAllLower = locale.toLowerCase();
  const supportedCountry = supportedCountries.find(
    (country) => country.locale === localeAllLower
  );

  if (!supportedCountry) {
    return true;
  }

  return !supportedCountry.products.some(
    (supportedProduct) => supportedProduct.name === product
  );
};

export function getCountryCode(product, locale) {
  if (product?.includes('-idc')) {
    /*
     "country", in the case of this function, is used to tell the user that the content they're viewing -- for the 
     card they have selected -- is related to the international dollar card rather than one specic country.
    */
    return 'International Dollar Card';
  }
  const lowerCaseLocale = locale ? locale.toLowerCase() : 'en-us';
  const supportedCountry = supportedCountries.find(
    (country) => country.locale === lowerCaseLocale
  );

  // return country code if available, if not full country name
  return supportedCountry?.code || supportedCountry?.name || 'US';
}

export function getQueryParams(name) {
  if (typeof window === 'undefined') return null;

  const url = new URL(window.location.href);
  return url.searchParams.get(name);
}

export function getCardArtUrl({
  baseOneCmsImgUrl,
  product,
  locale,
  isMedium = false,
}) {
  // handle default case with no locale query param
  const medium = isMedium ? '-medium' : '';
  const imageLocale = locale || 'en-US';
  return `${baseOneCmsImgUrl}card-art/${product}_${imageLocale.toLocaleLowerCase()}${medium}.png`;
}

export function resizeSelectElementToCurrentValue(select) {
  const newSelect = select;
  // remove each <option> except the current value
  const selectValues = [];
  newSelect.querySelectorAll('option').forEach((option) => {
    selectValues.push(option);
    if (option.getAttribute('value') !== newSelect.value) {
      newSelect.removeChild(option);
    }
  });

  // allow the <select> to auto-size
  newSelect.style.width = null;

  // copy that width to "freeze" it
  newSelect.style.width = `${newSelect.clientWidth + 20}px`;

  // restore the <select>'s children
  newSelect.innerText = '';
  selectValues.forEach((option) => newSelect.appendChild(option));
  return newSelect;
}

export function getBasePathFromLocation(locale) {
  return `/${locale.toLocaleLowerCase()}/travel/lounges`;
}

export function getMarketName(locale) {
  const [lang = 'en', region = 'US'] = locale.split('-');
  const countryName = Intl.DisplayNames
    ? new Intl.DisplayNames([lang], {
        type: 'region',
        style: 'short',
      }).of(region.toUpperCase())
    : region;

  return countryName;
}

/**
 * Formats text by replacing variables in the text with their corresponding values.
 *
 * @param {string} text Text to format.
 * @param {object} variables Object containing variables to replace in the text.
 * @returns {string} Text with variables replaced.
 */
export function formatText(text, variables) {
  let formattedText = text;
  Object.keys(variables).forEach((key) => {
    formattedText = formattedText.replace(
      new RegExp(`{${key}}`, 'g'),
      variables[key]
    );
  });
  return formattedText;
}

export const setAXPLocaleCookie = (locale) => {
  if (!locale || typeof document === 'undefined') return;
  try {
    document.cookie = `axplocale=${locale}; path=/ ;domain=.americanexpress.com`;
  } catch (error) {
    // this can fail if the cookie is blocked
    // eslint-disable-next-line no-console -- helpful error in the terminal or console
    console.error(`setAXPLocaleCookie error: ${error?.message}`);
  }
};

/**
 * Returns the Lounge Finder product based on product type in the user/profile api response.
 *
 * @param {Array} types MYCA product types returned via the user profile api.
 * @param {string} lang Language code associated with the account from MYCA (EN, ES, etc)
 * @returns {string} Lounge Finder product name.
 */
export const getProduct = (types, lang) => {
  if (!Array.isArray(types)) {
    return Card.Platinum;
  }

  if (
    types.includes(mycaCardTypes.platinum) &&
    types.includes(mycaCardTypes.internationalDollar) &&
    lang === langs.EN
  ) {
    return Card.PlatinumIDCEN;
  }

  if (
    types.includes(mycaCardTypes.platinum) &&
    types.includes(mycaCardTypes.internationalDollar) &&
    lang === langs.ES
  ) {
    return Card.PlatinumIDCES;
  }

  if (types.includes(mycaCardTypes.platinum)) {
    return Card.Platinum;
  }
  if (types.includes(mycaCardTypes.centurion)) {
    return Card.Centurion;
  }
  if (types.includes(mycaCardTypes.deltaReserve)) {
    return Card.Delta;
  }

  return Card.Platinum;
};
/**
 * Filters and sorts cards based on eligibility, type priority, and line of business priority.
 *
 * @param {Array} wallet Array of card objects.
 * @returns {Array} Sorted array of eligible and prioritized cards.
 */
export const getEligibleAndPrioritizedCards = (wallet) => {
  const cardTypesByPriority = [
    'the-centurion-card',
    'the-platinum-card',
    'the-platinum-card-idc-en',
    'the-platinum-card-idc-es',
    'delta-reserve',
  ];
  const lineOfBusinessPriority = [
    lineOfBusinessTypes.Consumer,
    lineOfBusinessTypes.Business,
    lineOfBusinessTypes.Corporate,
  ];

  return wallet
    .filter((card) => card.hasLoungeBenefit && card.active)
    .sort((a, b) => {
      // Sort by card type priority
      const typePriorityA = cardTypesByPriority.indexOf(a?.loungeFinderProduct);
      const typePriorityB = cardTypesByPriority.indexOf(b?.loungeFinderProduct);

      if (typePriorityA !== typePriorityB) {
        return typePriorityA - typePriorityB;
      }

      // If types are the same, sort by line of business priority
      const lobPriorityA = lineOfBusinessPriority.indexOf(
        a.lineOfBusinessTypeName
      );
      const lobPriorityB = lineOfBusinessPriority.indexOf(
        b.lineOfBusinessTypeName
      );

      return lobPriorityA - lobPriorityB;
    });
};

export const sortAndIdentifyCardProducts = (accounts) => {
  const identifiedAccountProducts = accounts.map((acct) => {
    const loungeFinderProduct = getProduct(
      acct?.product?.type,
      acct?.languagePreferenceCode
    );
    return {
      ...acct,
      loungeFinderProduct,
    };
  });

  const identifiedGuestBenefitEligibleAccounts = identifiedAccountProducts.map(
    (acct) => {
      const guestBenefitEligible = isGuestBenefitEligibleAccount(acct);
      return {
        ...acct,
        guestBenefitEligible,
      };
    }
  );

  const sortedAccounts = getEligibleAndPrioritizedCards(
    identifiedGuestBenefitEligibleAccounts
  );

  return sortedAccounts;
};

/**
 * Splits the Access & Guest policy header to be used in the Lounge Finder.
 *
 * @param {string} header agTab property from the OneCMS data (Access & Guests).
 * @returns {Array} Array with values split (ex: [Access, Guests]).
 */
export const splitAccessAndGuestPolicyHeader = (header) => {
  // Across all locales, aside from the exception locales: zh-cn, zh-hk, zh-tw, and ja-jp the "ag_tab" property from the OneCMS data contains (in various languages) the word "Access", the word "Guest", and either the word "and" or an ampersand (surrounded by space characters) between those words. In those exception locales, there's no space before/after the "and" word/symbol. This Regex covers all those current scenarios
  // eslint-disable-next-line unicorn/better-regex  -- the RegEx ES Lint Rule wants to swap "and" with "adn" then remove the vertical pipe that separates the different strings we want to remove. Functionally it works the same, but it makes it difficult for other devs to understand that the goal is to split where " and " or " & " is found in the string.
  return (header || '').split(/\s[&|and|e|y|et|und]+\s|及|與/);
};

/**
 * Returns a boolean value indicating whether the lounge's provider is Priority Pass.
 *
 * @param {array} accessRules Lounge Access Rules from the Lounges API.
 * @returns {Boolean} true if the lounge's provider is Priority Pass, false otherwise.
 */
export const isPriorityPassLounge = (accessRules) => {
  return accessRules?.[0]?.provider_id === 'PROVIDER_PRIORITY_PASS';
};

/**
 * Adds a trailing slash to a path if missing.
 * @param {string} path
 * @returns {string}
 */
export function addTrailingSlashIfMissing(path) {
  if (typeof path === 'string' && path.length > 0 && !path.endsWith('/')) {
    return `${path}/`;
  }
  return path;
}

/**
 * Builds a URL string with a trailing slash on the pathname, plus search and hash.
 * @param {string} pathname
 * @param {string} [search]
 * @param {string} [hash]
 * @returns {string}
 */
export function getUrlWithTrailingSlash(pathname, search = '', hash = '') {
  const pathWithSlash = addTrailingSlashIfMissing(pathname);
  const searchPart = search && search !== '?' ? `${search}` : '';
  const hashPart = hash && hash !== '#' ? `${hash}` : '';
  return `${pathWithSlash}${searchPart}${hashPart}`;
}

const getCurrentPathWithSearch = () => {
  if (typeof window === 'undefined') return '';
  return `${window.location.pathname}${window.location.search}`;
};

export const saveScrollPositionForRestore = ({ storageKey }) => {
  if (typeof window === 'undefined' || !storageKey) return;

  const payload = {
    path: getCurrentPathWithSearch(),
    scrollY: window.scrollY,
    timestamp: Date.now(),
  };

  window.sessionStorage.setItem(storageKey, JSON.stringify(payload));
};

export const restoreScrollPosition = ({
  storageKey,
  ttlMs = WAITLIST_SCROLL_RESTORE_TTL_MS,
}) => {
  if (typeof window === 'undefined' || !storageKey) return;

  const savedValue = window.sessionStorage.getItem(storageKey);
  if (!savedValue) return;

  const clearSavedValue = () => {
    window.sessionStorage.removeItem(storageKey);
  };

  try {
    const { path, scrollY, timestamp } = JSON.parse(savedValue);
    const isStale = Date.now() - timestamp > ttlMs;

    if (isStale) {
      clearSavedValue();
      return;
    }

    if (path !== getCurrentPathWithSearch()) {
      return;
    }

    if (typeof scrollY === 'number') {
      window.scrollTo(0, scrollY);
      // Refresh timestamp so TTL resets on each restore (keeps entry fresh during session)
      const updatedPayload = { path, scrollY, timestamp: Date.now() };
      window.sessionStorage.setItem(storageKey, JSON.stringify(updatedPayload));
    }
  } catch {
    clearSavedValue();
  }
};

export const saveScrollBeforeExternalNavigation = ({ storageKey }) => {
  try {
    saveScrollPositionForRestore({ storageKey });
  } catch {
    // Allow navigation to continue if session storage is blocked.
  }
};

/**
 * Filter Centurion providers in `the-platinum-card`, `the-centurion-card` etc files parners array so only one is displayed
 * on the partner tiles and home page terms & conditions modal. (each country centurion provider is a separate array entry)
 * (PROVIDER_AMEX_BR_GRU is also a Centurion Provider)
 * @param {array} partners partners array from the OneCMS card specific files
 * @returns {array}
 */
export function filterCenturionProvidersForDisplay(partners) {
  let foundCenturionProvider = false;
  const filteredPartners = partners.filter((partner) => {
    if (
      partner.provider_id?.includes('CENTURION') ||
      partner.provider_id === 'PROVIDER_AMEX_BR_GRU'
    ) {
      if (!foundCenturionProvider) {
        foundCenturionProvider = true;
        return true;
      }
      return false;
    }
    return true;
  });

  return filteredPartners;
}
